import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getBulkInvoiceHTMLWeb, getInvoiceHTMLWeb } from "./invoiceTemplateWeb";

export const generateInvoicePDF = async (orderData) => {
  try {
    // ── 1. Build the HTML string ──────────────────────────────
    const html = getInvoiceHTMLWeb(orderData);

    // ── 2. Create an off-screen container (same trick as before) ──
    const container = document.createElement("div");

    // Must be visible to html2canvas — do NOT use zIndex -1 or opacity 0
    // Position it off-screen instead so the user never sees it
    container.style.position = "fixed";
    container.style.top = "-9999px";
    container.style.left = "-9999px";
    container.style.width = "794px";
    container.style.minHeight = "1122px";
    container.style.background = "#fff";
    container.style.overflow = "hidden";
    // No zIndex trick — html2canvas needs the element to be "renderable"

    container.innerHTML = html;
    document.body.appendChild(container);

    // ── 3. Wait for fonts/images to settle ───────────────────
    await new Promise((res) => setTimeout(res, 600));

    // ── 4. Capture with html2canvas (same settings as working code) ──
    // REPLACE WITH:
    const canvas = await html2canvas(container, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      width: 794,
      scrollX: 0,
      scrollY: 0,
    });

    // ── 5. Build PDF ──────────────────────────────────────────
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth(); // 210 mm
    const pageHeight = pdf.internal.pageSize.getHeight(); // 297 mm

    const imgData = canvas.toDataURL("image/jpeg", 0.82);
    const imgWidthMm = pageWidth;
    const imgHeightMm = (canvas.height / canvas.width) * imgWidthMm;

    if (imgHeightMm <= pageHeight) {
      // Single page — fits perfectly
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidthMm, imgHeightMm);
    } else {
      // Multi-page support: slice the canvas into A4-sized chunks
      const pxPerMm = canvas.width / pageWidth;
      const pageHeightPx = pageHeight * pxPerMm;
      let remainingHeight = canvas.height;
      let yOffset = 0;
      let isFirstPage = true;

      while (remainingHeight > 0) {
        const sliceHeight = Math.min(pageHeightPx, remainingHeight);

        // Create a slice canvas
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, -yOffset);

        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.82);
        const sliceHeightMm = (sliceHeight / canvas.width) * pageWidth;

        if (!isFirstPage) pdf.addPage();
        pdf.addImage(sliceData, "JPEG", 0, 0, pageWidth, sliceHeightMm);
        yOffset += sliceHeight;
        remainingHeight -= sliceHeight;
        isFirstPage = false;
      }
    }

    // ── 6. Clean up & return blob ─────────────────────────────
    document.body.removeChild(container);

    const pdfBlob = pdf.output("blob");

    const orderNo =
      orderData?.orderNumber?.replace(/[\/\\?%*:|"<>]/g, "_") || "ORD";
    const quoNo = (
      orderData?.quotation?.quotationId ||
      orderData?.quotation?._id?.slice(-6) ||
      "QUO"
    ).replace(/[\/\\?%*:|"<>]/g, "_");
    const docType = orderData?.isProforma ? "Proforma" : "Invoice";
    const fileName = `pcm_${orderNo}_${quoNo}_${docType}.pdf`;

    return { blob: pdfBlob, fileName };
  } catch (err) {
    console.error("[generateInvoicePDF] Error:", err);
    return null;
  }
};

export const generateBulkInvoicePDF = async ({
  order,
  quotations = [],
  isSampleReq = true,
  isProforma = true,
}) => {
  try {
    const html = getBulkInvoiceHTMLWeb({
      order,
      quotations,
      isSampleReq,
      isProforma,
    });

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "-9999px";
    container.style.left = "-9999px";
    container.style.width = "794px";
    container.style.minHeight = "1122px";
    container.style.background = "#fff";
    container.style.overflow = "hidden";
    container.innerHTML = html;
    document.body.appendChild(container);

    await new Promise((res) => setTimeout(res, 600));

    const canvas = await html2canvas(container, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      width: 794,
      scrollX: 0,
      scrollY: 0,
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL("image/jpeg", 0.82);
    const imgHeightMm = (canvas.height / canvas.width) * pageWidth;

    if (imgHeightMm <= pageHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgHeightMm);
    } else {
      const pxPerMm = canvas.width / pageWidth;
      const pageHeightPx = pageHeight * pxPerMm;
      let remaining = canvas.height;
      let yOffset = 0;
      let first = true;

      while (remaining > 0) {
        const sliceH = Math.min(pageHeightPx, remaining);
        const sc = document.createElement("canvas");
        sc.width = canvas.width;
        sc.height = sliceH;
        sc.getContext("2d").drawImage(canvas, 0, -yOffset);
        const sliceData = sc.toDataURL("image/jpeg", 0.82);
        const sliceHMm = (sliceH / canvas.width) * pageWidth;
        if (!first) pdf.addPage();
        pdf.addImage(sliceData, "JPEG", 0, 0, pageWidth, sliceHMm);
        yOffset += sliceH;
        remaining -= sliceH;
        first = false;
      }
    }

    document.body.removeChild(container);

    const pdfBlob = pdf.output("blob");
    const orderNo = (order?.orderNumber || "ORD").replace(
      /[\/\\?%*:|"<>]/g,
      "_",
    );
    const fileName = `pcm_bulk_${orderNo}_Sample_Proforma.pdf`;

    return { blob: pdfBlob, fileName };
  } catch (err) {
    console.error("[generateBulkInvoicePDF] Error:", err);
    return null;
  }
};

/**
 * Triggers browser download of a blob as a named file.
 */
export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
